import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { listDevScenes } from '../../engine/dev/scenes.js';

/**
 * @file The title screen. One level, one button, everything else in a footer.
 *
 * The GTA logo look is three ingredients and no artwork: the display face
 * (Bowlby One SC, self-hosted, OFL — see `src/styles.css`), a hard black
 * keyline built from eight offset text-shadows, and one hard coloured drop
 * shadow. `.gta-title` in the stylesheet owns all of it. Nothing here imitates
 * a real game's marks — the name, the palette and the shapes are ours.
 *
 * The dev links stay. They are the review surface this project is built on
 * (`/play/vehicles` is the mandatory car review wall, `/play/bestiary` the
 * cast, `/gallery/city` the archetypes), so they get a quiet footer rather
 * than the front page they used to have.
 */

/** The game. There is exactly one level, by owner decision. */
const GAME = { id: 'city', title: 'HARBOURSIDE', tagline: 'An island. A grid. A very bad afternoon.' };

/**
 * Resolves `src/levels/index.js` lazily. The glob (rather than a static import)
 * is what keeps this route buildable while the level registry is being
 * rewritten — a missing module resolves to `{}` instead of failing the bundle.
 */
const levelIndexGlob = import.meta.glob('../../levels/index.js');

function useLevelRegistry() {
  // `null` = not loaded yet / doesn't exist.
  const [levels, setLevels] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const key = Object.keys(levelIndexGlob)[0];
    if (!key) return undefined;
    levelIndexGlob[key]()
      .then((mod) => {
        if (!cancelled) setLevels(mod.LEVELS ?? mod.default ?? null);
      })
      .catch((err) => console.warn('[MenuRoute] levels/index.js failed to load', err));
    return () => {
      cancelled = true;
    };
  }, []);
  return levels;
}

export default function MenuRoute() {
  const levels = useLevelRegistry();
  const [devScenes, setDevScenes] = useState([]);
  const ready = !!levels?.[GAME.id];

  useEffect(() => {
    setDevScenes(listDevScenes());
  }, []);

  return (
    <div className="relative h-full w-full overflow-auto bg-void text-hud">
      {/* Backdrop: one radial pool of light and a set of slow diagonal bars.
          Pure CSS — a title screen must not pull an asset. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(120% 80% at 50% 8%, rgba(255,193,10,0.10) 0%, rgba(7,8,12,0) 55%), radial-gradient(90% 70% at 50% 100%, rgba(45,226,230,0.07) 0%, rgba(7,8,12,0) 60%)',
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.055]"
        style={{
          backgroundImage:
            'repeating-linear-gradient(115deg, #ffffff 0 2px, transparent 2px 16px)',
        }}
      />

      <div className="relative mx-auto flex min-h-full max-w-4xl flex-col items-center px-6 py-16">
        <header className="mt-10 text-center">
          <h1 className="gta-title select-none font-display text-[13vw] leading-[0.92] sm:text-7xl md:text-8xl">
            {GAME.title}
          </h1>
          <p className="mt-8 font-hud text-sm uppercase tracking-[0.42em] text-hud-dim">{GAME.tagline}</p>
        </header>

        <div className="mt-14 flex flex-col items-center gap-3">
          {ready ? (
            <Link
              to={`/play/${GAME.id}`}
              onMouseDown={(e) => e.preventDefault()}
              className="group relative overflow-hidden rounded-[3px] border-2 border-gold bg-gold/10 px-16 py-3.5 font-display text-3xl uppercase tracking-[0.08em] text-gold transition hover:bg-gold hover:text-void"
            >
              Play
            </Link>
          ) : (
            <div className="cursor-not-allowed rounded-[3px] border-2 border-hairline px-16 py-3.5 font-display text-3xl uppercase tracking-[0.08em] text-hud-faint">
              Play
            </div>
          )}
          <p className="h-4 font-hud text-[11px] uppercase tracking-[0.28em] text-hud-faint">
            {ready ? 'WASD to move · Q/E or drag to turn · Esc to pause' : 'Loading level registry…'}
          </p>
        </div>

        <footer className="mt-auto w-full pt-20">
          {/* CREDITS SITS ON ITS OWN LINE, ABOVE THE DEV CHIPS AND NOT AMONG
              THEM. The row below is auto-discovered and grows every time
              somebody adds a dev scene, so a link dropped into it is a link
              that gets a little harder to find every week — and this one
              carries a CC BY 4.0 attribution that is a licence condition, not
              a nicety. Same `FootLink` chip, its own row. */}
          <div className="mb-5 flex items-center justify-center">
            <FootLink to="/credits">Credits</FootLink>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2">
            <FootLink to="/play/vehicles">Vehicles gallery</FootLink>
            <FootLink to="/play/bestiary">Bestiary</FootLink>
            <FootLink to="/gallery/city">City gallery</FootLink>
            {devScenes
              .filter((s) => s !== 'vehicles' && s !== 'bestiary')
              .map((sceneId) => (
                <FootLink key={sceneId} to={`/play/${sceneId}`}>
                  {sceneId}
                </FootLink>
              ))}
            {/* A plain <a>, not a <Link>: the WebGL2 fallback is decided when
                the renderer is created from the query string, so this has to
                be a real navigation. CLAUDE.md calls it a required smoke test —
                do not quietly drop it again. */}
            <a
              href="/play/test?forceWebGL=1"
              className="rounded-[3px] border border-hairline/70 px-2.5 py-1 font-hud text-[12px] uppercase tracking-[0.14em] text-hud-faint transition hover:border-neon/60 hover:text-neon"
            >
              test ?forceWebGL=1
            </a>
          </div>
          <p className="mt-8 text-center font-hud text-[11px] uppercase tracking-[0.3em] text-hud-faint/70">
            three.js r185 · WebGPU · CPU-baked vertex lighting · CC0 textures ambientCG
          </p>
        </footer>
      </div>
    </div>
  );
}

/**
 * Quiet footer chip. `onMouseDown` preventDefault everywhere, per the
 * project-wide rule about never stealing focus from a canvas.
 * @param {{to: string, children: any}} props
 */
function FootLink({ to, children }) {
  return (
    <Link
      to={to}
      onMouseDown={(e) => e.preventDefault()}
      className="rounded-[3px] border border-hairline/70 px-2.5 py-1 font-hud text-[12px] uppercase tracking-[0.14em] text-hud-faint transition hover:border-neon/60 hover:text-neon"
    >
      {children}
    </Link>
  );
}
